import React, { useEffect, useRef } from "react";
import "./AnimatedBanner.css";

// --- START: DYNAMIC CONFIGURATION ---
// Check if the screen width is less than 768px (a common mobile breakpoint)
const isMobile = window.innerWidth < 768;

// --- Configuration ---
const NODE_COUNT = isMobile ? 10 : 25; // Fewer nodes on mobile
const PARTICLE_COUNT = isMobile ? 100 : 500; // Drastically fewer particles on mobile
// --- END: DYNAMIC CONFIGURATION ---
const CONNECTION_DISTANCE = 180; // Max distance to draw a line between nodes
const NODE_SPEED = 0.2;
const PARTICLE_SPEED = 0.4;
const ATTRACTION_FORCE = 0.05; // How strongly particles are pulled to nodes

function AnimatedBanner() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    let frameId;
    let nodes = [];
    let particles = [];

    const resizeCanvas = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };

    const init = () => {
      nodes = [];
      for (let i = 0; i < NODE_COUNT; i++) {
        nodes.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          vx: (Math.random() - 0.5) * NODE_SPEED,
          vy: (Math.random() - 0.5) * NODE_SPEED,
          radius: 2 + Math.random() * 2,
        });
      }

      particles = [];
      for (let i = 0; i < PARTICLE_COUNT; i++) {
        particles.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
        });
      }
    };

    const animate = () => {
      // Fading background for the trailing effect
      ctx.fillStyle = "rgba(18, 18, 18, 0.1)";
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Update and draw connections between nodes
      nodes.forEach((nodeA) => {
        nodes.forEach((nodeB) => {
          if (nodeA === nodeB) return;
          const distance = Math.hypot(nodeA.x - nodeB.x, nodeA.y - nodeB.y);

          if (distance < CONNECTION_DISTANCE) {
            const opacity = 1 - distance / CONNECTION_DISTANCE;
            ctx.beginPath();
            ctx.moveTo(nodeA.x, nodeA.y);
            ctx.lineTo(nodeB.x, nodeB.y);
            ctx.strokeStyle = `rgba(0, 188, 212, ${opacity * 0.5})`;
            ctx.stroke();
          }
        });
      });

      // Update and draw nodes
      nodes.forEach((node) => {
        node.x += node.vx;
        node.y += node.vy;

        if (node.x < 0 || node.x > canvas.width) node.vx *= -1;
        if (node.y < 0 || node.y > canvas.height) node.vy *= -1;

        ctx.beginPath();
        ctx.arc(node.x, node.y, node.radius, 0, Math.PI * 2);
        ctx.fillStyle = "#00bcd4";

        // Add a glow effect to nodes
        ctx.shadowColor = "#00bcd4";
        ctx.shadowBlur = 10;
        ctx.fill();
        ctx.shadowBlur = 0; // Reset blur for other elements
      });

      // Update and draw particles
      particles.forEach((p) => {
        let closestNode = null;
        let minDistance = Infinity;

        nodes.forEach((node) => {
          const distance = Math.hypot(p.x - node.x, p.y - node.y);
          if (distance < minDistance) {
            minDistance = distance;
            closestNode = node;
          }
        });

        if (closestNode) {
          const angle = Math.atan2(closestNode.y - p.y, closestNode.x - p.x);
          const force =
            (1 - minDistance / CONNECTION_DISTANCE) * ATTRACTION_FORCE;

          p.x += Math.cos(angle) * PARTICLE_SPEED + (Math.random() - 0.5) * 0.5;
          p.y += Math.sin(angle) * PARTICLE_SPEED + (Math.random() - 0.5) * 0.5;

          // Attraction logic
          if (minDistance < CONNECTION_DISTANCE) {
            p.x += (closestNode.x - p.x) * force;
            p.y += (closestNode.y - p.y) * force;
          }
        }

        // Reset particle if it goes off-screen
        if (p.x < 0 || p.x > canvas.width || p.y < 0 || p.y > canvas.height) {
          p.x = Math.random() * canvas.width;
          p.y = Math.random() * canvas.height;
        }

        const hue = (p.x / canvas.width) * 60 + 180; // Colors from cyan to blue
        ctx.fillStyle = `hsl(${hue}, 100%, 70%)`;
        ctx.fillRect(p.x, p.y, 1, 1);
      });

      frameId = requestAnimationFrame(animate);
    };

    const handleResize = () => {
      resizeCanvas();
      init();
    };

    window.addEventListener("resize", handleResize);

    resizeCanvas();
    init();
    animate();

    return () => {
      window.removeEventListener("resize", handleResize);
      cancelAnimationFrame(frameId);
    };
  }, []);

  return <canvas ref={canvasRef} className="animated-banner-canvas" />;
}

export default AnimatedBanner;
